import React, { useEffect, useState } from "react";

import {
  Button,
  PageContainer,
  Sidebar,
  SpinnerCenter,
  TeamsLayoutContainer,
} from "@shapr3d/frontend-lib";
import classNames from "classnames";
import { useParams, useSearchParams } from "react-router-dom";

import HeaderContainer from "../../containers/HeaderContainer";
import useNavigation from "../../hooks/navigation";
import useAccount from "../../hooks/useAccount";
import { useChinaUser } from "../../hooks/useChinaUser";
import { type TeamIntentError, TeamIntentErrors, useTeamIntents } from "../../hooks/useTeamIntents";
import { useTeam } from "../../hooks/useTeams";
import useTranslation from "../../hooks/useTranslation";
import { Modal } from "../../lib/modal";
import { SearchParam } from "../../types";
import { PageSource } from "../../types/tracking";
import { DashboardNavGroup } from "./components/DashboardNavGroup/DashboardNavGroup";
import { SettingsNavGroup } from "./components/SettingsNavGroup/SettingsNavGroup";
import { TeamSwitcher } from "./components/TeamSwitcher/TeamSwitcher";

import style from "./TeamsLayout.module.css";

type TeamIntentBlockingModalParamType = {
  [key in TeamIntentError]: {
    readonly title: string;
    readonly message: string;
    readonly buttonText?: string;
  };
};

const TeamIntentBlockingModalParams: TeamIntentBlockingModalParamType = {
  [TeamIntentErrors.Unauthorized]: {
    title: "Unauthorized",
    message: "You are not authorized to perform this operation.",
    buttonText: "Close",
  },
  [TeamIntentErrors.TeamNotFound]: {
    title: "Unauthorized",
    message: "You are not authorized to perform this operation.",
    buttonText: "Close",
  },
  [TeamIntentErrors.RenameSpaceUnauthorized]: {
    title: "Edit space name",
    message: "Only team owners can rename spaces. Contact your team owner to rename this space.",
  },
};
export const NavBarContext = React.createContext({
  onSelect: () => {
    //
  },
});

export const TeamsLayout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigation = useNavigation();
  const { t, m } = useTranslation();
  const [searchParams] = useSearchParams();
  const { teamID } = useParams();

  const { teamData } = useTeam(teamID);

  const { data: accountData } = useAccount();
  const { isChinaUser, isLoading: isChinaUserLoading } = useChinaUser();

  const {
    error: teamIntentError,
    isLoading: teamIntentLoading,
    clear: clearTeamIntent,
  } = useTeamIntents({ teamID });

  useEffect(() => {
    if (accountData && !teamData) {
      if (isChinaUserLoading) return;
      const teamIDToNavigateTo =
        accountData.teams.find(({ isPrimary }) => isPrimary)?.id ?? accountData.teams[0].id;
      if (searchParams.get(SearchParam.Source) === PageSource.AuthHandoff) {
        navigation.goToTeam({
          teamID: teamIDToNavigateTo,
          replace: true,
          omitQueryParams: [SearchParam.Source],
        });
      } else if (isChinaUser && !accountData.hasChargebeeSubscription) {
        navigation.goToTeamPage({
          teamID: teamIDToNavigateTo,
          page: "subscription",
          replace: true,
        });
      } else {
        navigation.goToDefaultPage({ teamID: teamIDToNavigateTo, replace: true });
      }
    }
  }, [accountData, teamData, navigation, searchParams, isChinaUser, isChinaUserLoading]);

  if (!teamID || teamIntentLoading || teamIntentError || (isChinaUserLoading && !teamData)) {
    const teamIntentBlockingParams = teamIntentError
      ? TeamIntentBlockingModalParams[teamIntentError]
      : null;

    return (
      <>
        <HeaderContainer />
        <PageContainer>
          <SpinnerCenter />
        </PageContainer>
        <Modal
          open={Boolean(teamIntentBlockingParams)}
          telemetryName="teams_layout_intent_blocking"
          size="small"
          title={teamIntentBlockingParams?.title}
          onClose={() => {
            clearTeamIntent();
          }}
        >
          <div className={style.errorModalContainer}>
            <span>{teamIntentBlockingParams?.message}</span>
            {teamIntentBlockingParams?.buttonText && (
              <Button
                variant="primary"
                onClick={() => {
                  clearTeamIntent();
                }}
              >
                {teamIntentBlockingParams?.buttonText}
              </Button>
            )}
          </div>
        </Modal>
      </>
    );
  }

  return (
    <>
      <HeaderContainer
        className={style.header}
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
      />
      <NavBarContext.Provider value={{ onSelect: () => setSidebarOpen(false) }}>
        <TeamsLayoutContainer sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen}>
          <Sidebar className={classNames(style.sidebar)}>
            <Sidebar.Header>{t(m.TeamsLayout.Teams)}</Sidebar.Header>
            <Sidebar.SpacerItem variant="bottom">
              <TeamSwitcher onTeamSelect={() => setSidebarOpen(false)} />
            </Sidebar.SpacerItem>
            <DashboardNavGroup />
            <SettingsNavGroup />
          </Sidebar>
        </TeamsLayoutContainer>
      </NavBarContext.Provider>
    </>
  );
};
